import { useState } from "react";
import { FolderPlus, Check, Plus, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCollections } from "@/hooks/collections/use-collections";
import { useAddToCollection, useRemoveFromCollection, usePromptCollections } from "@/hooks/collections/use-collection-actions";
import { CreateCollectionDialog } from "./CreateCollectionDialog";

interface AddToCollectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promptId: string;
}

export function AddToCollectionModal({ open, onOpenChange, promptId }: AddToCollectionModalProps) {
  const { data: collections, isLoading: loadingCollections } = useCollections();
  const { data: promptCollections, isLoading: loadingPromptCollections } = usePromptCollections(promptId);
  const addToCollection = useAddToCollection();
  const removeFromCollection = useRemoveFromCollection();
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [pendingActions, setPendingActions] = useState<Set<string>>(new Set());

  const isInCollection = (collectionId: string) => {
    return promptCollections?.some((c) => c.id === collectionId) ?? false;
  };

  const handleToggle = async (collectionId: string) => {
    if (pendingActions.has(collectionId)) return;
    
    setPendingActions((prev) => new Set(prev).add(collectionId));
    
    try {
      if (isInCollection(collectionId)) {
        await removeFromCollection.mutateAsync({ collectionId, promptId });
      } else {
        await addToCollection.mutateAsync({ collectionId, promptId });
      }
    } finally {
      setPendingActions((prev) => {
        const next = new Set(prev);
        next.delete(collectionId);
        return next;
      });
    }
  };

  const isLoading = loadingCollections || loadingPromptCollections;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="h-5 w-5" />
              Add to Collection
            </DialogTitle>
          </DialogHeader>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !collections?.length ? (
            <div className="text-center py-6">
              <p className="text-muted-foreground mb-4">You don't have any collections yet.</p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Collection
              </Button>
            </div>
          ) : (
            <>
              <ScrollArea className="max-h-[300px]">
                <div className="space-y-2">
                  {collections.map((collection) => {
                    const inCollection = isInCollection(collection.id);
                    const isPending = pendingActions.has(collection.id);
                    
                    return (
                      <button
                        key={collection.id}
                        onClick={() => handleToggle(collection.id)}
                        disabled={isPending}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors disabled:opacity-50"
                      >
                        <span className="text-xl">{collection.emoji}</span>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-sm">{collection.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {collection.promptCount || 0} prompts
                          </p>
                        </div>
                        {isPending ? (
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        ) : inCollection ? (
                          <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>

              <Button
                variant="outline"
                className="w-full mt-2"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New Collection
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CreateCollectionDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </>
  );
}
